import './PromotionEditor.less';
import React, {useState} from 'react';
import __ from "../../i18n";
import CustomForm from "../../../bootstrap/components/custom-form/CustomForm";
import Request from "../../../bootstrap/api/Request";
import Toasts, {TOAST_SUCCESS} from "../../../bootstrap/scomponents/toasts/Toasts";
import PromotionCreateParcel from "../../api/endpoints/promotion/PromotionCreateParcel";
import PromotionUpdateParcel from "../../api/endpoints/promotion/PromotionUpdateParcel";
import {slugify} from '../../../bootstrap/utils/string';
import routes from "../../routing/routes";
import visibilities, {VISIBILITY_PUBLIC} from "../../model-constants/promotion/visibilities";
import {getToday} from '../../../bootstrap/utils/date-ymd';
import {dateTimeToString, extractDateFromString, extractTimeFromString} from '../../../bootstrap/utils/time-ymdhms';


export default function PromotionEditor({recordId = null, defaultValues = {}, onDone, onCancel}) {

    const isCreation = recordId === null;

    const [values, setValues] = useState(defaultValues);

    const slug = values.name ? slugify(values.name) : '';

    const fieldsConfig = {
        name: {
            name: __('Name'),
            type: 'text',
            defaultValue: defaultValues.name,
            rules: {
                required: true,
                maxLength: 191,
            },
            options: {
                appearance: 'box'
            },
            valueHint: slug !== '' ? __('URL: ::url', {url: routes.promotion.getUrl(slug)}) : undefined,
        },
        avatar_image_id: {
            name: __('Avatar image'),
            type: 'image',
            defaultValue: defaultValues.avatar_image_id,
            rules: {
                required: true
            }
        },
        banner_image_id: {
            name: __('Banner image'),
            type: 'image',
            defaultValue: defaultValues.banner_image_id,
            rules: {
                required: false
            }
        },
        description: {
            name: __('Description'),
            type: 'text',
            defaultValue: defaultValues.description,
            rules: {
                required: true,
                maxLength: 191
            },
            options: {
                appearance: 'box'
            }
        },
        discount_percentage: {
            name: __('Discount percentage'),
            type: 'number',
            defaultValue: defaultValues.discount_percentage || null,
            rules: {
                required: true,
                min: 1,
                max: 100,
            },
            options: {
                unit: '%',
                appearance: 'box',
                prompt: __('Percentage of original price for discount')
            },
        },
        start_time_ymd: {
            type: 'date',
            defaultValue: defaultValues.start_time !== undefined ? extractDateFromString( defaultValues.start_time) : getToday(),
            rules: {
                required: true,
            },
        },
        start_time_hms: {
            type: 'time',
            defaultValue: defaultValues.start_time !== undefined ? extractTimeFromString(defaultValues.start_time) : [0, 0, 0],
            rules: {
                required: true,
            },
            options: {
                hasSeconds: true,
            },
        },
        end_time_ymd: {
            type: 'date',
            defaultValue: defaultValues.end_time !== undefined ? extractDateFromString(defaultValues.end_time) : getToday(),
            rules: {
                required: true,
            },
        },
        end_time_hms: {
            type: 'time',
            defaultValue: defaultValues.end_time !== undefined ? extractTimeFromString(defaultValues.end_time) : [23, 59, 59],
            rules: {
                required: true,
            },
            options: {
                hasSeconds: true,
            },
        },
        visibility: {
            name: __('Visibility'),
            type: 'singleSelect',
            defaultValue: defaultValues.visibility || VISIBILITY_PUBLIC,
            rules: {
                required: true
            },
            data: {
                options: visibilities
            }
        },
        priority: {
            name: __('Priority'),
            type: 'number',
            defaultValue: defaultValues.priority || 0,
            rules: {
                required: true
            },
            options: {
                appearance: 'box',
                prompt: __('Higher priority will be placed before')
            },
        },
        introduction: {
            name: __('Introduction'),
            type: 'richText',
            defaultValue: defaultValues.introduction,
            rules: {
                required: true
            }
        }
    };

    const layoutConfig = [
        'name',
        {
            children: [
                'avatar_image_id',
                'banner_image_id',
            ],
            direction: 'row',
            alignItems: 'start',
            itemWeights: [0.5, 0.5],
        },
        'description',
        'discount_percentage',
        {
            children: [
                {
                    name: __('Start time'),
                    children: [
                        'start_time_ymd',
                        'start_time_hms',
                    ],
                    direction: 'row',
                    alignItems: 'start',
                    itemWeights: [0.5, 0.5],
                },
                {
                    name: __('End time'),
                    children: [
                        'end_time_ymd',
                        'end_time_hms',
                    ],
                    direction: 'row',
                    alignItems: 'start',
                    itemWeights: [0.5, 0.5],
                },
            ],
            direction: 'row',
            alignItems: 'start',
            itemWeights: [0.5, 0.5],
        },
        {
            children: [
                'visibility',
                'priority',
            ],
            direction: 'row',
            alignItems: 'start',
            itemWeights: [0.5, 0.5],
        },
        'introduction',
    ];

    const submit = (values, onSuccess, onError) => {

        const submittedValues = {
            ...values,
            start_time: dateTimeToString([...values.start_time_ymd, ...values.start_time_hms]),
            end_time: dateTimeToString([...values.end_time_ymd, ...values.end_time_hms]),
            slug
        };

        if (isCreation) {
            Request.add(new PromotionCreateParcel(submittedValues))
                .then((data) => {
                    Toasts.add(TOAST_SUCCESS, __('Your promotion was added successfully'));
                    onSuccess();
                    onDone(data);
                })
                .catch(error => {
                    onError(error.getMessage());
                });

            return;
        }

        Request.add(new PromotionUpdateParcel(recordId, submittedValues))
            .then((data) => {
                Toasts.add(TOAST_SUCCESS, __('Your changes have been saved successfully'));
                onSuccess();
                onDone(data);
            })
            .catch(error => {
                onError(error.getMessage());
            });
    };

    const cancel = () => {
        onCancel();
    };

    return <div className="promotion-editor">
        <div className="sub-title">{__('Promotion information')}</div>
        <CustomForm
            fieldsConfig={fieldsConfig}
            layoutConfig={layoutConfig}
            submit={submit}
            cancel={cancel}
            submitText={isCreation ? __('Add promotion') : __('Save changes')}
            onChange={setValues}
            onInitialized={setValues}
        />
    </div>;
}
